/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {MapPinIcon} from '@ion/dls/src/icons/MapPinIcon';
import {navigateToGoogleStreetView} from '@ion/dls/src/maps/helpers/globeUtils';
import {IconSize} from '@ion/dls/src/types/types';
import {TertiaryIconButton, TertiaryIconButtonProps} from './TertiaryIconButton';

export interface StreetViewIconButtonProps extends TertiaryIconButtonProps {
  assetData: CVSAssetType
  // opens the asset's street view in the app (e.g. its Street View dialog) -
  // without one, Google Street View opens in a new tab ...
  onStreetView?: (assetData: CVSAssetType) => void;
}

/**
 *
 * @StreetViewIconButton
 *
 * The StreetViewIconButton is a TertiaryIconButton that displays a pin
 * icon with "Street View" text. Icon placement is specified as it would be
 * for the TertiaryIconButton. Other TertiaryIconButton props may also be specified
 * (except "icon" and "label" props, which are fixed).
 *
 * ```typescript
 *
 * import React from 'react';
 * import {StreetViewIconButton} from '@ion/dls/src/components/buttons/';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <StreetViewIconButton assetData={assetData} iconDirection='left' />
 *        <br />
 *       <StreetViewIconButton assetData={assetData} />
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
export const StreetViewIconButton = (props: StreetViewIconButtonProps) => {
  const {assetData, onStreetView, ...baseProps} = props;

  return (
    <TertiaryIconButton {...baseProps}
      icon={<MapPinIcon size={IconSize.medium} />}
      label={translate('common:streetView')}
      onClick={function(e) {
        e.stopPropagation();
        e.preventDefault();
        if (onStreetView) {
          onStreetView(assetData);
        } else {
          navigateToGoogleStreetView(assetData);
        }
      }} />
  );
};

export default StreetViewIconButton;
